import { H2Heading } from "../../Headers";
import { ArticleParagraph } from "../../Paragraphs";
import { InfoBox } from "./InfoBox";
import { NonogramCell } from "./NonogramCell";
import { DISPLAY_CAP } from "./nonogramSolver";

const PlacementMiniRow = ({ placement }) => (
    <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max">
            {placement.map((value, index) => (
                <NonogramCell
                    key={index}
                    index={index}
                    userState={value === 'filled' ? 'filled' : 'crossed'}
                    interactive={false}
                    size="sm"
                />
            ))}
        </div>
    </div>
);

const DeducedMiniRow = ({ cells }) => (
    <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max">
            {cells.map((value, index) => (
                <NonogramCell
                    key={index}
                    index={index}
                    userState="unknown"
                    deducedState={value === 'ambiguous' ? null : value}
                    interactive={false}
                    size="sm"
                />
            ))}
        </div>
    </div>
);

export const StageCombinations = ({
    placements,
    placementsTruncated,
    totalValidCount,
    guaranteedCells,
    contradiction,
    enumerationOverflow
}) => {
    const displayed = placements.slice(0, DISPLAY_CAP);

    return (
        <div className="my-8">
            <H2Heading>Stage 3 — Check every valid arrangement</H2Heading>

            <ArticleParagraph
                lines={[
                    "The overlap trick above is a shortcut. The authoritative answer comes from listing every arrangement that satisfies the clue and your marked cells, then checking which cells are filled — or empty — in absolutely all of them."
                ]}
            />

            {contradiction ? (
                <InfoBox title="No valid arrangement exists" type="warning">
                    None of the arrangements that satisfy this clue are consistent with the cells you've marked. Clear a mark on the row above to see valid arrangements again.
                </InfoBox>
            ) : (
                <>
                    {enumerationOverflow && (
                        <InfoBox title="Too many arrangements to enumerate" type="warning">
                            This clue and row length allow more valid arrangements than can be checked live, so the guaranteed cells below fall back to the Stage 2 overlap result instead of the full authoritative check.
                        </InfoBox>
                    )}

                    <p className="text-sm text-gray-700 mb-3">
                        {totalValidCount} valid {totalValidCount === 1 ? 'arrangement' : 'arrangements'} found{placementsTruncated ? `, showing the first ${DISPLAY_CAP}` : ''}:
                    </p>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="space-y-1">
                            {displayed.map((placement, i) => (
                                <PlacementMiniRow key={i} placement={placement} />
                            ))}
                        </div>
                        {placementsTruncated && (
                            <p className="text-xs text-gray-500 italic pt-2">
                                + {totalValidCount - DISPLAY_CAP} more valid {totalValidCount - DISPLAY_CAP === 1 ? 'arrangement' : 'arrangements'} not shown — the answer below is still computed across all of them.
                            </p>
                        )}

                        <div className="border-t border-gray-300 mt-3 pt-3">
                            <p className="text-xs font-medium text-badger-purple mb-1">
                                Guaranteed in every arrangement above{placementsTruncated ? ' (and all the rest not shown)' : ''}:
                            </p>
                            <DeducedMiniRow cells={guaranteedCells} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
